import { useEffect, useState, useRef } from "react";
import { db } from "@/lib/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import useEmblaCarousel from 'embla-carousel-react';
import { toast } from "@/hooks/use-toast";
import { useParams } from "react-router-dom";

interface Video {
  id: string;
  name: string;
  url: string;
  type: string;
  description?: string;
}

export default function VideoFeed() {
  const { folderId } = useParams<{ folderId: string }>();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [folderName, setFolderName] = useState<string>("");

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: 'y',
    loop: true,
    draggable: true,
    dragFree: false,
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Синхронизация ref-массива
  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, videos.length);
  }, [videos]);

  // Intersection Observer — играет только видимое видео
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            video.currentTime = 0;
            video.play().catch((e) => {
              console.log("Автозапуск заблокирован:", e);
            });
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      },
      {
        threshold: [0.0, 0.7], // реагируем на 0% и 70% видимости
        rootMargin: "0px",     // строго по границам экрана
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const loadFolderAndVideos = async () => {
      if (!folderId) {
        setLoading(false);
        return;
      }

      try {
        const folderDocRef = doc(db, "diskFolders", folderId);
        const folderSnap = await getDoc(folderDocRef);

        if (!folderSnap.exists()) {
          toast({ title: "Папка не найдена", variant: "destructive" });
          setLoading(false);
          return;
        }

        const folderData = folderSnap.data();
        const folderPath = folderData?.path || "";
        const displayName = folderData?.name || "Без названия";

        setFolderName(displayName);

        const filesQuery = query(
          collection(db, "diskFiles"),
          where("path", "==", folderPath),
          where("type", "==", "video/mp4")
        );

        const snapshot = await getDocs(filesQuery);
        const videoList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }) as Video);

        setVideos(videoList);
      } catch (err) {
        console.error("Ошибка загрузки:", err);
        toast({ title: "Ошибка загрузки видео", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    loadFolderAndVideos();
  }, [folderId]);

  // Embla + play/pause (вспомогательная логика, основная — в Observer)
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const currentIndex = emblaApi.selectedScrollSnap();
      console.log("Текущее видео (Embla):", currentIndex);
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("settle", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("settle", onSelect);
    };
  }, [emblaApi]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 flex items-center justify-center text-white">
        <p className="text-xl animate-pulse">Загрузка видео...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <h1 className="text-2xl font-bold text-center py-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent absolute top-0 left-0 right-0 z-10 pointer-events-none">
        Видео в папке "{folderName}"
      </h1>

      <div className="fixed inset-0 w-screen h-screen overflow-hidden touch-pan-y" ref={emblaRef}>
        <div className="flex flex-col h-full snap-y snap-mandatory">
          {videos.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-purple-300 text-xl">
              В этой папке пока нет видео
            </div>
          ) : (
            videos.map((video, index) => (
              <div
                key={video.id}
                className="flex-shrink-0 flex-grow-0 basis-full h-screen relative flex items-center justify-center bg-black snap-start"
              >
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                    if (el) observerRef.current?.observe(el);
                  }}
                  src={video.url}
                  className="w-full h-full object-cover" // ← object-cover решает проблему перезапуска
                  controls
                  loop
                  playsInline
                  preload="auto"
                />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-20 z-20 pointer-events-none">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">{video.name}</h2>
                  <p className="text-base md:text-lg opacity-90">
                    {video.description || "Без описания"}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}